import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKey } from "@phosphor-icons/react";

export default function Profile() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.employee_id) api.get(`/employees/${user.employee_id}`).then(({ data }) => setEmp(data));
  }, [user?.employee_id]);

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated successfully.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Your profile</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{user?.name}</h1>
        <p className="text-sm text-zinc-500 mt-2">{user?.email}</p>
      </div>

      <div className="border border-zinc-200 bg-white rounded-lg p-6 space-y-3">
        <Row label="Role" value={user?.role} />
        <Row label="Tenant" value={user?.tenant?.name || (user?.role === "super_admin" ? "Agency" : "—")} />
        {emp && (
          <>
            <Row label="Employee code" value={emp.employee_code} />
            <Row label="Department" value={emp.department} />
            <Row label="Designation" value={emp.designation} />
            <Row label="Grade" value={emp.grade} />
            <Row label="Reporting manager" value={emp.reporting_manager_id ? "Set" : "—"} />
            <Row label="Location" value={emp.location} />
            <Row label="Joining" value={emp.date_of_joining} />
            {emp.skill_tags?.length > 0 && (
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Skills</div>
                <div className="flex flex-wrap gap-1.5">
                  {emp.skill_tags.map((s) => <Badge key={s} variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300">{s}</Badge>)}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="border border-zinc-200 bg-white rounded-lg p-6" data-testid="change-password-card">
        <div className="flex items-center gap-2 mb-4">
          <LockKey size={18} className="text-emerald-700" />
          <h2 className="font-display text-xl font-bold tracking-tight">Change password</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Current password</Label>
            <Input type="password" value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              className="rounded-md" data-testid="current-password-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">New password</Label>
            <Input type="password" value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              className="rounded-md" data-testid="new-password-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Confirm new password</Label>
            <Input type="password" value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="rounded-md" data-testid="confirm-password-input" />
          </div>
        </div>
        <Button onClick={changePassword} disabled={saving}
          className="mt-4 btn-zenith rounded-md" data-testid="change-password-btn">
          {saving ? "Updating…" : "Update password"}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-t border-zinc-100 first:border-t-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-900">{value || "—"}</div>
    </div>
  );
}
